import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Portal from "@/pages/Portal";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/Privacy";
import Insights from "@/pages/Insights";
import Article from "@/pages/Article";
import MonthlyBookkeeping from "@/pages/MonthlyBookkeeping";
import AuditPreparation from "@/pages/AuditPreparation";
import Accounting from "@/pages/Accounting";
import QuickBooksCleanup from "@/pages/QuickBooksCleanup";
import { trackPageview } from "@/lib/analytics";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, behavior: "instant" });
    trackPageview();
  }, [pathname, hash]);
  return null;
};

const NotFound = () => (
  <section data-testid="not-found" className="flex min-h-[70vh] flex-col items-start justify-center px-6 pt-36 lg:px-10">
    <p className="font-serif text-7xl italic text-pine/30">404</p>
    <h1 className="mt-4 font-serif text-4xl tracking-tighter text-ink">This page slipped off the ledger.</h1>
    <Link
      to="/"
      data-testid="not-found-home-button"
      className="mt-8 inline-flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
    >
      Back to home
    </Link>
  </section>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-paper text-ink">
        <div className="grain-overlay" aria-hidden="true" />
        <ScrollToTop />
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/services/monthly-bookkeeping" element={<MonthlyBookkeeping />} />
            <Route path="/services/audit-preparation" element={<AuditPreparation />} />
            <Route path="/services/accounting" element={<Accounting />} />
            <Route path="/services/quickbooks-cleanup" element={<QuickBooksCleanup />} />
            <Route path="/insights/:slug" element={<Article />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <Toaster position="bottom-right" richColors />
      </div>
    </BrowserRouter>
  );
}

export default App;
